// src/components/RemovedSections.jsx
import { useState } from "react";
import {
  Card,
  List,
  Button,
  Typography,
  Space,
  Divider,
  Collapse,
  InputNumber,
} from "antd";
import { UndoOutlined, ScissorOutlined } from "@ant-design/icons";
import * as THREE from "three";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import { removeTrianglesInSphere } from "../ThreeJs/Trees/removeTree";
import { TREES, TREESPHERES } from "../ThreeJs/Global/variables"; // ✅ added
const { Text } = Typography;
const { Panel } = Collapse;

const RemovedSections = ({ removedSections, setRemovedSections }) => {
  const [expandedSphereIndex, setExpandedSphereIndex] = useState(null);

  // ✅ SAME logic as ToolHeader: remove drawn trees (cone/circle) intersecting the sphere
  const removeDrawnTreesInsideSphere = (worldSphere) => {
    try {
      if (!TREES?.trees || TREES.trees.length === 0) return 0;

      let removed = 0;
      const box = new THREE.Box3();
      const treeSphere = new THREE.Sphere();

      for (let i = TREES.trees.length - 1; i >= 0; i--) {
        const tree = TREES.trees[i];
        if (!tree) continue;

        box.setFromObject(tree);
        box.getBoundingSphere(treeSphere);

        const intersects =
          worldSphere.intersectsSphere(treeSphere) ||
          worldSphere.containsPoint(treeSphere.center);

        if (!intersects) continue;

        if (window.transformControls?.object === tree) {
          try {
            window.transformControls.detach();
          } catch (e) {}
        }

        tree.parent?.remove(tree);

        tree.traverse((o) => {
          if (o.geometry) o.geometry.dispose?.();
          if (o.material) {
            if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
            else o.material.dispose?.();
          }
        });

        TREES.trees.splice(i, 1);
        removed++;
      }

      return removed;
    } catch (e) {
      console.error("Failed to remove drawn trees inside sphere:", e);
      return 0;
    }
  };

  // ✂️ Trim logic (UPDATED to match ToolHeader behavior)
  const handleTrimWithSphere = (sphere, index) => {
    const houseGroup = sceneManager.scene.getObjectByName("houseModel");
    if (!sphere || !houseGroup) return;

    const radius = 1 * sphere.scale.x;
    const worldSphere = new THREE.Sphere(sphere.position.clone(), radius);

    // 1) Trim model
    const removedSection = removeTrianglesInSphere(houseGroup, worldSphere);
    if (removedSection) {
      setRemovedSections((prev) => ({
        ...prev,
        trimmedSections: [...(prev.trimmedSections || []), removedSection],
      }));
    }

    // 2) Trim drawn trees (cone/circle)
    const removedTreesCount = removeDrawnTreesInsideSphere(worldSphere);

    // ✅ remove the sphere from global TREESPHERES too (prevents re-using deleted sphere)
    if (TREESPHERES?.spheres?.length) {
      const idx = TREESPHERES.spheres.indexOf(sphere);
      if (idx !== -1) TREESPHERES.spheres.splice(idx, 1);
    }

    // ✅ Clean up controls + remove sphere + remove from sidebar list
    window.removeSphereAndControls?.(sphere);

    // Also remove from removedSections.spheres UI list so it disappears immediately
    setRemovedSections((prev) => ({
      ...prev,
      spheres: prev.spheres.filter((s) => s !== sphere),
    }));

    // optional feedback
    if (!removedSection && removedTreesCount === 0) {
      // nothing to trim
      return;
    }
  };

  // 🔄 Undo trimmed section (model-only, as you currently support)
  const handleUndoTrimmed = (index) => {
    const section = removedSections.trimmedSections[index];
    const houseModel = sceneManager.scene.getObjectByName("houseModel");
    if (houseModel && section) {
      houseModel.add(section);
      setRemovedSections((prev) => ({
        ...prev,
        trimmedSections: prev.trimmedSections.filter((_, i) => i !== index),
      }));
    }
  };

  // 🎛️ Update sphere dimensions
  const handleSphereChange = (index, key, value) => {
    setRemovedSections((prev) => {
      const updated = [...prev.spheres];
      const sphere = updated[index];
      if (!sphere) return prev;

      if (key === "radius") {
        sphere.scale.set(value, value, value);
      } else if (key === "width") sphere.scale.x = value;
      else if (key === "length") sphere.scale.y = value;
      else if (key === "height") sphere.scale.z = value;

      return { ...prev, spheres: updated };
    });
  };

  // 🧩 When user expands a sphere, attach controls to it
  const handleExpandSphere = (index, sphere) => {
    const newIndex = expandedSphereIndex === index ? null : index;
    setExpandedSphereIndex(newIndex);
    if (newIndex !== null && sphere) {
      window.attachControlsToSphere?.(sphere);
    }
  };

  return (
    <Card size="small" style={{ width: 375 }} bodyStyle={{ padding: 0 }}>
      <div style={{ padding: "8px 14px" }}>
        <Text strong style={{ fontSize: 14 }}>
          Tree Spheres
        </Text>
      </div>

      <Collapse
        bordered={false}
        activeKey={expandedSphereIndex !== null ? [expandedSphereIndex] : []}
        expandIconPosition="end"
      >
        {removedSections.spheres.map((sphere, index) => (
          <Panel
            key={index}
            header={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                onClick={() => handleExpandSphere(index, sphere)}
              >
                <Text>{index + 1}. Sphere</Text>
              </div>
            }
          >
            <div style={{ marginBottom: 8, paddingLeft: 16, paddingRight: 16 }}>
              <Text type="secondary">Radius:</Text>
              <InputNumber
                min={0.1}
                max={20}
                step={0.1}
                value={sphere.scale.x}
                onChange={(val) => handleSphereChange(index, "radius", val)}
                style={{ width: "100%", marginTop: 4 }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "space-between",
                marginBottom: 8,
                paddingLeft: 16,
                paddingRight: 16,
              }}
            >
              {[
                { label: "W", key: "width", value: sphere.scale.x },
                { label: "L", key: "length", value: sphere.scale.y },
                { label: "H", key: "height", value: sphere.scale.z },
              ].map((dim) => (
                <div
                  key={dim.key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "33%",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 12, marginBottom: 2 }}>
                    {dim.label}
                  </Text>
                  <InputNumber
                    min={0.1}
                    step={0.1}
                    value={dim.value}
                    onChange={(val) => handleSphereChange(index, dim.key, val)}
                    style={{ width: "100%" }}
                  />
                </div>
              ))}
            </div>

            <Button
              type="primary"
              danger
              icon={<ScissorOutlined />}
              style={{ width: "91.5%", marginLeft: 16, marginRight: 16 }}
              onClick={() => handleTrimWithSphere(sphere, index)}
            >
              Trim Tree
            </Button>
          </Panel>
        ))}
      </Collapse>

      <Divider style={{ margin: "10px 0" }} />
      <div style={{ padding: "0px 14px" }}>
        <Text strong style={{ fontSize: 14 }}>
          Trimmed Sections
        </Text>
      </div>

      <List
        dataSource={removedSections.trimmedSections}
        locale={{ emptyText: "No trimmed sections removed" }}
        renderItem={(item, index) => (
          <List.Item
            style={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Space>
              <Text>{index + 1}. Section</Text>
            </Space>
            <Space>
              <Button
                type="text"
                icon={<UndoOutlined />}
                onClick={() => handleUndoTrimmed(index)}
              />
            </Space>
          </List.Item>
        )}
      />
    </Card>
  );
};

export default RemovedSections;
